// ExamplesPage.js — Phase PC.9
//
// Public web destination for the iOS Prompt Catalog. Surfaces the same
// `endProducts where contentCategory='prompt'` Firestore docs the in-app
// gallery uses (PC.4-5). Triple-duty:
//   1. Web discovery — anyone can browse the catalog without installing the app
//   2. SEO + landing-page social proof for the App Store page
//   3. Single source of truth for marketing assets (the same JSON and GCS
//      previews drive in-app, web, and screenshot pipelines)
//
// Card click opens a modal with the full prompt text, a Copy button, and an
// "Open in mChatAI" CTA that deep-links into the iOS app on iPhone. The tap
// also shows an App Store link for people who don't have the app yet.
//
// Routes wired in App.js:
//   /examples                — all prompts, applet tabs at the top
//   /examples/dreamsnap       — DreamSnap (image) prompts only
//   /examples/audio-library   — AI Audio (music) prompts only

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { fetchEndProducts } from './communityApi';

const API_BASE = process.env.REACT_APP_API_URL || 'https://web-api-irnuiiphsq-uc.a.run.app';

const APPLETS = [
  { id: 'dreamsnap',            label: 'DreamSnap',  kind: 'image',  emoji: '🪄' },
  { id: 'audio-library',        label: 'AI Audio',   kind: 'audio',  emoji: '🎵' },
  { id: 'audio-library-speech', label: 'TTS Speech', kind: 'speech', emoji: '🎙️' },
  { id: 'dreamsnap-video',      label: 'AI Video',   kind: 'video',  emoji: '🎬' },
];

const APP_STORE_LINK = 'https://apps.apple.com/us/app/mchatai/id6445939409?platform=iphone';
const IOS_DEEP_LINK_BASE = 'mchatai://prompts';
// Phase PC.9.2 — canonical Universal Link (matched by /.well-known/apple-app-site-association).
// External shares (Twitter, email, Messages) of this URL open directly in the
// installed app. Within mchatai.com itself we still fire the URL scheme first
// since Safari doesn't deep-link same-origin clicks via Universal Links.
const UNIVERSAL_LINK_BASE = 'https://mchatai.com/examples';

// ── Bulk rating fetch (parallel chunks of 50, silent on per-chunk failure) ──

async function fetchBulkSummaries(slugs) {
  if (!slugs.length) return {};
  const chunks = [];
  for (let i = 0; i < slugs.length; i += 50) chunks.push(slugs.slice(i, i + 50));
  const out = {};
  await Promise.all(chunks.map(async (chunk) => {
    try {
      const resp = await fetch(API_BASE + '/v1/ratings/summary-bulk?slugs=' + encodeURIComponent(chunk.join(',')));
      const json = await resp.json();
      if (json.ok && json.summaries) Object.assign(out, json.summaries);
    } catch (e) { /* silent */ }
  }));
  return out;
}

// ── Prompt card ──────────────────────────────────────────────────────────────

function PromptCard({ product, ratingSummary, onOpen }) {
  const isImage = product.subType === 'dreamsnap';
  const audioURL = product.audioPreviewURL;
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  function togglePreview(e) {
    e.stopPropagation();
    if (!audioURL) return;
    if (playing) { audioRef.current?.pause(); setPlaying(false); return; }
    if (!audioRef.current) audioRef.current = new Audio(audioURL);
    audioRef.current.onended = () => setPlaying(false);
    audioRef.current.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }

  useEffect(() => () => { audioRef.current?.pause(); }, []);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(product)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(product); } }}
      style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14,
        padding: 0, display: 'flex', flexDirection: 'column',
        boxSizing: 'border-box', overflow: 'hidden',
        transition: 'border-color 0.12s, transform 0.1s, box-shadow 0.12s', cursor: 'pointer',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.06)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ position: 'relative', aspectRatio: '1/1', background: '#f3f4f6' }}>
        {isImage && product.iconURL && (
          <img src={product.iconURL} alt="" loading="lazy" decoding="async"
               style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        )}
        {!isImage && (
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,#6366f1aa,#ec4899aa)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <div style={{ fontSize: 40, color: '#fff', opacity: 0.9 }}>♪</div>
            {audioURL ? (
              <button onClick={togglePreview}
                style={{ background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {playing ? '◼ Stop' : '▶ Preview'}
              </button>
            ) : (
              <span style={{ background: 'rgba(0,0,0,0.4)', color: '#fff', borderRadius: 999, padding: '2px 10px', fontSize: 11 }}>No preview</span>
            )}
          </div>
        )}
        {ratingSummary?.count > 0 && (
          <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
            ★ {ratingSummary.avg.toFixed(1)}
          </div>
        )}
      </div>
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {product.name}
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.35, height: 28, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {product.summary || ''}
        </div>
      </div>
    </div>
  );
}

// ── Modal: full prompt + copy + open in app ──────────────────────────────────

function PromptModal({ product, onClose }) {
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showStoreLink, setShowStoreLink] = useState(false);
  const isImage = product.subType === 'dreamsnap';
  // Phase PC.9.2 — universal link for this prompt; shareable anywhere,
  // opens the iOS app directly when the recipient has it installed.
  const universalLink = `${UNIVERSAL_LINK_BASE}/${product.subType}/${product.slug}`;

  async function copyPrompt() {
    try {
      await navigator.clipboard.writeText(product.prompt || product.description || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* fallback: select & copy not built; tap-and-hold the field also works */ }
  }

  async function copyShareLink() {
    try {
      await navigator.clipboard.writeText(universalLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1800);
    } catch { /* no-op */ }
  }

  function openInApp() {
    // iOS has no store fallback for a custom scheme, and the page cannot tell
    // Safari's "Open in mChatAI?" prompt from its "address is invalid" alert:
    // the page stays visible under both. So the tap never jumps to the App
    // Store on a timer. That would cancel the prompt for the people who have
    // the app. Instead it shows the store link under the buttons.
    const url = `${IOS_DEEP_LINK_BASE}/${product.subType}/${product.slug}`;
    window.location.href = url;
    setShowStoreLink(true);
  }

  // Opening the app hides the page, so the store line is no longer needed.
  useEffect(() => {
    if (!showStoreLink) return undefined;
    function onVisibility() { if (document.hidden) setShowStoreLink(false); }
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [showStoreLink]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  return (
    <div role="dialog" aria-modal="true"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 16, maxWidth: 560, width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        {isImage && product.iconURL && (
          <img src={product.iconURL} alt="" style={{ width: '100%', maxHeight: 360, objectFit: 'cover', display: 'block', borderTopLeftRadius: 16, borderTopRightRadius: 16 }} />
        )}
        {!isImage && (
          <div style={{ height: 200, background: 'linear-gradient(135deg,#6366f1,#ec4899)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderTopLeftRadius: 16, borderTopRightRadius: 16 }}>
            {product.audioPreviewURL ? (
              <audio controls src={product.audioPreviewURL} style={{ width: '90%' }} />
            ) : (
              <span style={{ color: '#fff', fontSize: 14 }}>No audio preview available</span>
            )}
          </div>
        )}
        <div style={{ padding: 20 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: '#111827' }}>{product.name}</h2>
          <div style={{ marginTop: 6, fontSize: 13, color: '#6b7280' }}>
            {APPLETS.find((a) => a.id === product.subType)?.label || product.subType}
            {product.modelHint && ` · ${product.modelHint}`}
            {product.publisherName && ` · by ${product.publisherName}`}
          </div>
          {product.summary && <p style={{ marginTop: 10, color: '#374151', fontSize: 14, lineHeight: 1.45 }}>{product.summary}</p>}
          <div style={{ marginTop: 14, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, fontSize: 13, color: '#1f2937', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>
            {product.prompt || product.description}
          </div>
          {Array.isArray(product.tags) && product.tags.length > 0 && (
            <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {product.tags.map((t) => (
                <span key={t} style={{ background: '#eef2ff', color: '#4338ca', fontSize: 11, padding: '3px 8px', borderRadius: 999 }}>#{t}</span>
              ))}
            </div>
          )}
          <div style={{ marginTop: 18, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={copyPrompt}
              style={{ flex: '1 1 140px', background: copied ? '#10b981' : '#111827', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {copied ? '✓ Copied!' : 'Copy prompt'}
            </button>
            <button onClick={openInApp}
              style={{ flex: '1 1 140px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Open in mChatAI →
            </button>
            <button onClick={copyShareLink}
              title={universalLink}
              style={{ flex: '0 1 140px', background: linkCopied ? '#10b981' : '#fff', color: linkCopied ? '#fff' : '#374151', border: '1px solid ' + (linkCopied ? '#10b981' : '#e5e7eb'), borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
              {linkCopied ? '✓ Link copied' : 'Copy link'}
            </button>
            <button onClick={onClose}
              style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
              Close
            </button>
          </div>
          {showStoreLink && (
            <p style={{ margin: '12px 2px 0', fontSize: 13, color: '#6b7280', lineHeight: 1.45 }}>
              Don’t have mChatAI? <a href={APP_STORE_LINK} style={{ color: '#3b82f6' }}>Get it on the App Store</a>,
              then come back here and tap <b>Open in mChatAI</b> again.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function ExamplesPage() {
  const { appletId: routeApplet, slug: routeSlug } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeApplet, setActiveApplet] = useState(routeApplet || 'all');
  const [tagFilter, setTagFilter] = useState('');
  const [openProduct, setOpenProduct] = useState(null);

  // Keep URL + state in sync when the user clicks an applet tab.
  useEffect(() => { setActiveApplet(routeApplet || 'all'); }, [routeApplet]);

  // Fetch on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const all = await fetchEndProducts({ category: 'prompt' });
        if (cancelled) return;
        // Sort by rating desc, then by tag-rich first.
        all.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
        setProducts(all);
        const slugs = all.map((p) => p.slug).filter(Boolean);
        const summ = await fetchBulkSummaries(slugs);
        if (!cancelled) setSummaries(summ);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    let xs = products;
    if (activeApplet !== 'all') xs = xs.filter((p) => p.subType === activeApplet);
    if (tagFilter) xs = xs.filter((p) => (p.tags || []).includes(tagFilter));
    return xs;
  }, [products, activeApplet, tagFilter]);

  // Phase PC.9.2 — when the URL is /examples/{appletId}/{slug} (Universal Link
  // landing for desktop visitors, or in-page deep link), find the matching
  // product once products load and auto-open the modal so the share URL is
  // meaningful on web too. iOS Universal Links route through the AASA before
  // ever hitting this page; this only fires on desktop or in-app web fallback.
  useEffect(() => {
    if (!routeSlug || !products.length) return;
    const match = products.find((p) =>
      p.slug === routeSlug && (!routeApplet || p.subType === routeApplet)
    );
    if (match) setOpenProduct(match);
  }, [routeSlug, routeApplet, products]);

  const topTags = useMemo(() => {
    const counts = {};
    for (const p of filtered) for (const t of p.tags || []) counts[t] = (counts[t] || 0) + 1;
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([t]) => t);
  }, [filtered]);

  function setApplet(id) {
    setTagFilter('');
    if (id === 'all') navigate('/examples');
    else navigate(`/examples/${id}`);
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px 64px' }}>
      <header style={{ marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 28, color: '#111827' }}>Prompt Examples</h1>
        <p style={{ margin: '6px 0 0', color: '#6b7280', fontSize: 14, maxWidth: 640 }}>
          Curated and community-published prompts for the mChatAI iOS apps.
          Tap a card to read the full prompt, copy it, or open it directly
          in DreamSnap or AI Audio.
        </p>
      </header>

      <nav style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {[{ id: 'all', label: 'All', emoji: '✨' }, ...APPLETS].map((a) => (
          <button key={a.id} onClick={() => setApplet(a.id)}
            style={{ padding: '8px 14px', borderRadius: 999,
                     background: activeApplet === a.id ? '#111827' : '#fff',
                     color: activeApplet === a.id ? '#fff' : '#374151',
                     border: '1px solid ' + (activeApplet === a.id ? '#111827' : '#e5e7eb'),
                     fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {a.emoji} {a.label}
          </button>
        ))}
      </nav>

      {topTags.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
          {tagFilter && (
            <button onClick={() => setTagFilter('')}
              style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', fontSize: 11, padding: '3px 10px', borderRadius: 999, cursor: 'pointer', fontWeight: 600 }}>
              Clear: #{tagFilter} ✕
            </button>
          )}
          {topTags.map((t) => (
            <button key={t} onClick={() => setTagFilter(t === tagFilter ? '' : t)}
              style={{ background: t === tagFilter ? '#3b82f6' : '#eef2ff',
                       color: t === tagFilter ? '#fff' : '#4338ca',
                       border: 'none', fontSize: 11, padding: '3px 10px', borderRadius: 999,
                       cursor: 'pointer', fontWeight: 500 }}>
              #{t}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 80, textAlign: 'center', color: '#6b7280' }}>Loading examples…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 80, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
          No prompts here yet. <Link to="/examples" style={{ color: '#3b82f6' }}>Show all</Link>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {filtered.map((p) => (
            <PromptCard key={p._id || p.id} product={p}
              ratingSummary={summaries[p.slug] || { avg: p.averageRating || 0, count: p.ratingCount || 0 }}
              onOpen={setOpenProduct} />
          ))}
        </div>
      )}

      {openProduct && <PromptModal product={openProduct} onClose={() => setOpenProduct(null)} />}

      <footer style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid #e5e7eb', color: '#6b7280', fontSize: 12, textAlign: 'center' }}>
        Want to add your own?{' '}
        <a href={APP_STORE_LINK} style={{ color: '#3b82f6' }}>Get the iOS app</a> and tap “Save & Share Prompt” after generating.
      </footer>
    </div>
  );
}
